import { type ComponentProps, type ReactNode } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "../../lib/utils";
import { AvatarImage } from "./AvatarImage";
import { AvatarFallback, type AvatarFallbackConfigProps } from "./AvatarFallback";
import { avatarVariants, type AvatarSize } from "./styles";

export interface AvatarProps
  extends Omit<ComponentProps<typeof AvatarPrimitive.Root>, "children" | "asChild" | keyof AvatarFallbackConfigProps>,
    AvatarFallbackConfigProps {
  /** Omit (or pass a falsy value) to render only the fallback. */
  src?: string | null;
  alt?: string;
  /** The fallback, rendered while `src` is loading, missing, or fails to load. Typically initials. */
  children: ReactNode;
  /**
   * Sizes the circle and scales the fallback initials to match. `full` fills the containing element.
   * @default "md"
   */
  size?: AvatarSize;
  fallbackClassName?: string;
  /**
   * Delay before the fallback becomes visible, so a fast-loading image doesn't flash it.
   * @default 0
   */
  fallbackDelayMs?: number;
}

export const Avatar = ({
  className,
  src,
  alt,
  id,
  size,
  children,
  fallbackClassName,
  fallbackDelayMs,
  enableStaticBackground,
  enableDeterministicColor,
  deterministicColorMode,
  ...props
}: AvatarProps) => (
  <AvatarPrimitive.Root data-slot="avatar" className={cn(avatarVariants({ size }), className)} {...props}>
    {src ? <AvatarImage src={src} alt={alt} /> : null}
    <AvatarFallback
      id={id}
      size={size}
      className={fallbackClassName}
      delayMs={fallbackDelayMs}
      enableStaticBackground={enableStaticBackground}
      enableDeterministicColor={enableDeterministicColor}
      deterministicColorMode={deterministicColorMode}
    >
      {children}
    </AvatarFallback>
  </AvatarPrimitive.Root>
);
